/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { API_URI, MIDDLE_END_URI, SERVERLESS_API_URI } from '../constants'
import { readCookie } from '../utils/cookies'

const getApiUri = (endpoint: string) => {
  return `${API_URI}${endpoint}`
}

const getServerlessApiUri = (endpoint: string) => {
  return `${SERVERLESS_API_URI}${endpoint}`
}

const getMiddleEndUri = (endpoint: string) => {
  return `${MIDDLE_END_URI}${endpoint}`
}

const getICloudApiUri = () => {
  return `${MIDDLE_END_URI}/calendar/iCloud/`
}

const getOutlookApiUri = (code: string) => {
  return `${MIDDLE_END_URI}/calendar/outlook/?code=${code}`
}

const getHeaders = () => {
  const myHeaders = new Headers()

  myHeaders.append('Content-Type', 'application/x-www-form-urlencoded')
  myHeaders.append('Accept', 'application/json')

  const token = readCookie('token')
  if (token && token !== '') myHeaders.append('user-token', token)

  const affiliate = readCookie('affiliate')
  if (affiliate && affiliate !== '') myHeaders.append('affiliate', affiliate)

  return myHeaders
}

const toFormBody = (body: any): string => {
  const formBody = []
  for (const property in body) {
    const encodedKey = encodeURIComponent(property)
    const encodedValue = encodeURIComponent(body[property])
    formBody.push(encodedKey + '=' + encodedValue)
  }
  return formBody.join('&')
}

export const uploadFile = (file: any, bucket: string) => {
  const fd = new FormData()
  fd.append('photo', file)
  fd.append('bucket', bucket)

  return fetch(getMiddleEndUri('/image/companyImage/'), {
    method: 'POST',
    body: fd,
  }).then((response) => response.json())
}

const get = (url: string) => {
  return fetch(getApiUri(url), {
    headers: getHeaders(),
  }).then((response) => response.json())
}

const getServerless = (url: string) => {
  return fetch(getServerlessApiUri(url), {
    headers: getHeaders(),
  }).then((response) => response.json())
}

const post = (url: string, body?: any) => {
  return fetch(getApiUri(url), {
    method: 'POST',
    headers: getHeaders(),
    body: toFormBody(body),
  }).then((response) => response.json())
}

export const getCurrentMeetings = () => {
  return get('/meeting/current')
}

export const getPastMeetings = () => {
  return get('/meeting/history')
}

export const getSettings = () => {
  return get('/settings/get')
}

export const getPage = (company: string, pageName: string) => {
  return get(`/company/pages?company=${company}&pageName=${pageName}`)
}

export const claimCompany = (body: any) => {
  return post('/company/claim', body)
}

export const savePage = (body: any) => {
  return post('/company/page', body)
}

export const saveCompanyEmployees = (body: any) => {
  return post('/company/employees', body)
}

export const saveCompanyInfo = (body: any) => {
  return post('/company/info', body)
}

export const deletePage = (body: any) => {
  return post('/page/deletePage', body)
}

export const getDefaultTimezone = (timezone: string) => {
  return get(`/settings/getTimezone?timezone=${timezone}`)
}

export const postUpdateDefaultLocation = (body: any) => {
  return post('/location/default', body)
}

export const postUpdateName = (body: any) => {
  return post('/settings/name', body)
}

export const postUpdateDefaultCalendar = (body: any) => {
  return post('/settings/defaultAgenda', body)
}

export const postUpdateAvailability = (body: any) => {
  return post('/settings/availability', body)
}

export const postUpdateDays = (body: any) => {
  return post('/settings/days', body)
}

export const getAvailabilities = (meeting_id: string) => {
  return get(`/meeting/availabilities/?meeting_id=${meeting_id}`)
}

export const getAllAvailabilities = (page_id: number) => {
  return get(`/page/possible/?pageId=${encodeURIComponent(page_id)}`)
}

export const deleteMeeting = (body: any) => {
  return post('/meeting/delete', body)
}

export const createLinkMeeting = (body: any) => {
  return post('/page/event', body)
}

export const sendResponse = (body: any) => {
  return post('/meeting/response', body)
}

export const outlookAuth = (code: string) => {
  return fetch(getOutlookApiUri(code), {
    headers: getHeaders(),
  }).then((response) => response.json())
}

export const iCloudAuth = (body: any) => {
  return fetch(getICloudApiUri(), {
    method: 'POST',
    headers: getHeaders(),
    body: toFormBody(body),
  }).then((response) => response.json())
}

export const postUpdateInformation = (body: any) => {
  return post('/settings/information', body)
}

export const oneClickSignon = (body: any) => {
  body.timezone = 'Europe/London'
  return fetch(getApiUri('/auth/oneclicksignon'), {
    method: 'POST',
    headers: getHeaders(),
    body: toFormBody(body),
    credentials: 'include',
  }).then((response) => response.json())
}

export const postAddAgendas = (body: any) => {
  const headers = new Headers()
  headers.append('Content-Type', 'application/x-www-form-urlencoded')
  headers.append('Accept', 'application/json')
  headers.append('user-token', readCookie('token') ?? '')

  return fetch(getApiUri('/calendar/agendas/selected'), {
    method: 'POST',
    headers,
    body: 'agendas=' + JSON.stringify(body),
  }).then((response) => response.json())
}

export const postMeetingResponse = (body: any) => {
  return post('/meeting/meetingResponse', body)
}

export const postMeetingOwnerResponse = (body: any) => {
  return post('/meeting/ownerResponse', body)
}

export const postPreferredTimes = (body: any) => {
  return post('/settings/preferred_times', body)
}

export const zoomConnect = (body: any) => {
  return post('/integration/zoomConnect', body)
}

export const postAction = (body: any) => {
  return post('/integration/action', body)
}

export const addSubscription = () => {
  return getServerless('/subscriptions')
}

export const requestCompanyAddition = (body: any) => {
  return post('/company/requestCompanyAddition', body)
}

export const approveEmployee = (
  companyId: string,
  employeeId: string,
  key: string,
) => {
  return get(
    `/company/approveCompanyRequest?companyId=${companyId}&employeeId=${employeeId}&key=${key}`,
  )
}

export const cancelSubscription = () => {
  return post('/payment/cancel')
}

export const removeCalendar = (calendarId: string) => {
  return post('/calendar/removeAgendaAndCalendar', {
    calendarId,
  })
}

export const claimPage = (body: any) => {
  return post('/page/claim', body)
}
